import { createSelector } from 'reselect';
import { every, find, get, toPairs } from 'lodash';
import { PERMITTED_PERMISSIONS } from 'modules/common/constants/permisssionsTypes';
import * as appointmentPermissionResources from 'modules/common/constants/appointmentPermissionResources';

const getAppointment = state => get(state, 'appointmentInfo.currentAppointment');

export const USERS = 'USERS';
export const USERS_DETAILS = 'USERS_DETAILS';
export const USERS_ORGANIZATION = 'USERS_ORGANIZATION';
export const USERS_SPECIALITIES = 'USERS_SPECIALITIES';
export const USERS_SHIFTS = 'USERS_SHIFTS';
export const USERS_SETTINGS = 'USERS_SETTINGS';
export const USERS_ACTIVITY_LOG = 'USERS_ACTIVITY_LOG';

export const getUserPermissions = (roleResources = [], type = 'read') => ({
  canSeeUser: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS), type)),
  canSeeDetails: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_DETAILS), type)),
  canSeeActivityLog: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_ACTIVITY_LOG), type)),
  canSeeOrganization: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_ORGANIZATION), type)),
  canSeeSettings: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_SETTINGS), type)),
  canSeeSpecialties: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_SPECIALITIES), type)),
  canSeeShifts: ['UNIT', 'DEEP', 'ASSIGNEE_UNIT', 'ASSIGNEE_DEEP'].includes(get(roleResources.find(item => item.resourceCode === USERS_SHIFTS), type)),
});

export const userPermissionsResources = {
  USERS,
  USERS_DETAILS,
  USERS_ORGANIZATION,
  USERS_SPECIALITIES,
  USERS_SHIFTS,
  USERS_SETTINGS,
  USERS_ACTIVITY_LOG,
};

const checkUserPermission = (permissions, neededPermissions) => {
  const [resourceCode, properties] = toPairs(neededPermissions)[0];
  const roleResources = get(permissions, 'roleResources', []);
  const resource = find(roleResources, { resourceCode });

  return every(properties, property => PERMITTED_PERMISSIONS.includes(get(resource, property)));
};

export const getAppointmentUsersReadPermissions = (permissions, resource) => {
  const userDetailsPermissions = checkUserPermission(permissions, { [resource]: ['read'] });

  return {
    canSeePolicyId: userDetailsPermissions,
    canSeeName: userDetailsPermissions,
    canSeeGender: userDetailsPermissions,
    canSeeBirthDate: userDetailsPermissions,
    canSeeEmail: userDetailsPermissions,
    canSeePhone: userDetailsPermissions,
    canSeeAddress: userDetailsPermissions,
    canSeeDescription: userDetailsPermissions,
    canSeeAvatar: userDetailsPermissions,
  };
};

export const getAppointmentUsersUpdatePermissions = (permissions, resource) => {
  const userDetailsPermissions = checkUserPermission(permissions, { [resource]: ['update'] });

  return {
    canUpdatePolicyId: false,
    canUpdateName: userDetailsPermissions,
    canUpdateGender: userDetailsPermissions,
    canUpdateBirthDate: userDetailsPermissions,
    canUpdateEmail: userDetailsPermissions,
    canUpdatePhone: userDetailsPermissions,
    canUpdateAdditionalPhone: userDetailsPermissions,
    canUpdateAddress: userDetailsPermissions,
    canUpdateDescription: userDetailsPermissions,
    canUpdateAvatar: userDetailsPermissions,
  };
};

export const getCustomerAppointmentUserReadPermissions = createSelector(
  [getAppointment],
  ({ permissions }) => getAppointmentUsersReadPermissions(permissions, appointmentPermissionResources.APPOINTMENTS_CUSTOMER),
);

export const getCustomerAppointmentUserUpdatePermissions = createSelector(
  [getAppointment],
  ({ permissions }) => getAppointmentUsersUpdatePermissions(permissions, appointmentPermissionResources.APPOINTMENTS_CUSTOMER),
);

export const getSubjectAppointmentUserReadPermissions = createSelector(
  [getAppointment],
  ({ permissions }) => getAppointmentUsersReadPermissions(permissions, appointmentPermissionResources.APPOINTMENTS_SUBJECTS),
);

export const getSubjectAppointmentUserUpdatePermissions = createSelector(
  [getAppointment],
  ({ permissions }) => getAppointmentUsersUpdatePermissions(permissions, appointmentPermissionResources.APPOINTMENTS_SUBJECTS),
);
